/**
 * Strings for Content Library pages.
 */
import {plural, t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import * as React from "react";

import {doNotTranslate} from "@khan/i18n";
import {logUnreachableCode} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import type {LearnableContentKind, MasteryLevel} from "../types/types.ts";

export const hours = (hours: number): string =>
    plural(hours, {
        one: "# hour",
        other: "# hours",
    });
export const minutes = (minutes: number): string =>
    plural(minutes, {
        one: "# minute",
        other: "# minutes",
    });
export const seconds = (seconds: number): string =>
    plural(seconds, {
        one: "# second",
        other: "# seconds",
    });
export const hourAndMinuteTime = (hours: string, minutes: string): string =>
    t`${hours} and ${minutes}`;
export const minuteAndSecondTime = (minutes: string, seconds: string): string =>
    t`${minutes} and ${seconds}`;

// Mastery Viz
export const titleAndLevel = (title: string, fpmLevelName: string): string =>
    t({
        context:
            'Example - "Evaluating expressions with one variable: Proficient"',
        message: `${title}: ${fpmLevelName}`,
    });
export const numberOfQuestions = (numberOfQuestions: number): string =>
    t({
        context: "The number of questions in a Quiz or Unit Test.",

        message: plural(numberOfQuestions, {
            one: "# question",
            other: "# questions",
        }),
    });
export const vizToolTipExerciseTitle = (title: string): string =>
    t`Exercise: ${title}`;
export const translatedTitleAndQuestionsCount = (
    title: string,
    count: number,
): string => {
    return plural(count, {
        one: `${title}: # question`,
        other: `${title}: # questions`,
    });
};
export const translatedQuizTitleAndQuestionsCount = (
    number: number,
    count: number,
): string => {
    return translatedTitleAndQuestionsCount(translatedQuizTitle(number), count);
};
export const translatedUnitTestTitleAndQuestionsCount = (
    count: number,
): string => {
    return translatedTitleAndQuestionsCount(t`Unit test`, count);
};

export const copyrightNotice: (arg1: number) => string = (currentYear) => {
    return doNotTranslate`© ${currentYear} Khan Academy`;
};

export const courseWithTitle = (courseName: string): string => {
    return t`Course: ${courseName}`;
};

type TranslatableContentKind = LearnableContentKind | "CourseMasteryChallenge";

export const translatedContentKind: (
    arg1: TranslatableContentKind,
) => string = (contentKind) => {
    switch (contentKind) {
        case "Article":
            return t`Article`;
        case "Challenge":
            return t`Challenge`;
        case "CourseMasteryChallenge":
            return t`Mastery challenge`;
        case "Exercise":
            return t`Exercise`;
        case "Interactive":
            return t`Interactive`;
        case "Project":
            return t`Project`;
        case "Quiz":
            return t`Quiz`;
        case "Talkthrough":
            return t`Talkthrough`;
        case "UnitTest":
            return t`Unit test`;
        case "Video":
            return t`Video`;
        case "AIGuideActivity":
            return t`AI Guide Activity`;
        default:
            throw logUnreachableCode("Unknown contentKind", contentKind);
    }
};

export const translatedMasteryLevel: (
    arg1?: MasteryLevel | null | undefined,
) => string = (level) => {
    switch (level) {
        case "familiar":
            return t`Familiar`;
        case "proficient":
            return t`Proficient`;
        case "mastered":
            return t`Mastered`;
        case "attempted":
            return t`Attempted`;
        case null:
        case undefined:
        case "unfamiliar":
            return t`Not started`;
        default:
            throw logUnreachableCode("Unknown progress state", level);
    }
};

export function translatedPercentage(percentage: number): string {
    return t`${percentage}%`;
}

export function translatedMasteryLevelAndPercentage(
    masteryLevel: MasteryLevel,
    percentage: number,
): string {
    switch (masteryLevel) {
        case "familiar":
            return t`Familiar • ${percentage}%`;
        case "proficient":
            return t`Proficient • ${percentage}%`;
        case "mastered":
            return t`Mastered • ${percentage}%`;
        case "attempted":
            return t`Attempted • ${percentage}%`;
        case "unfamiliar":
            return t`Not started`;
        default:
            throw logUnreachableCode("Unknown progress state", masteryLevel);
    }
}

export function translatedCourseChallengeTitleWithPercentage(
    percentage: number,
): string {
    return t`Course challenge: ${percentage}%`;
}

export function translatedQuizTitleWithPercentage(
    quizIndex: number,
    percentage: number,
): string {
    return t`Quiz ${quizIndex}: ${percentage}%`;
}

export function translatedUnitTestTitleWithPercentage(
    percentage: number,
): string {
    return t`Unit test: ${percentage}%`;
}

export function translatedQuizTitle(quizNumber: number): string {
    return t`Quiz ${quizNumber}`;
}

export function unitAndIndex(unitIndex: number): string {
    return t({
        context:
            "Link to a Unit in a course by with just a generic Unit descriptor and its index in the Course.",
        message: `Unit ${unitIndex}`,
    });
}

export function unitIndexAndTitle(
    unitIndex: number,
    unitTitle: string,
): string {
    return t`Unit ${unitIndex}: ${unitTitle}`;
}

const numberFormatter = new Intl.NumberFormat(RequestInfo.kaLocale);

export function unitsCount(unitCount: number): string {
    return t`${numberFormatter.format(unitCount)} units`;
}

export function skillsCount(skillCount: number): string {
    return t`${numberFormatter.format(skillCount)} skills`;
}

export function lessonNumberAndTitle(
    lessonNumber: number,
    title: string,
): string {
    return t({
        context:
            "Heading for a Lesson with the Lesson descriptor, its number in the Unit, and its title.",
        message: `Lesson ${lessonNumber}: ${title}`,
    });
}

export function assignmentDueDate(dueDate?: string | null): string {
    if (!dueDate) {
        return "";
    }
    const formatter = new Intl.DateTimeFormat(RequestInfo.kaLocale, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });

    return t`Due ${formatter.format(new Date(dueDate))}`;
}

export const nextLessonText = (lessonNumber: number): string =>
    t`Up next: Lesson ${lessonNumber}`;

export const nextUnitText = (unitNumber: number): string =>
    t`Up next: Unit ${unitNumber}`;

export const nextQuizText = (quizNumber: number): string =>
    t`Up next: Quiz ${quizNumber}`;

// Content title ARIA label when On Task but where we don't include the content
// kind in the label.
export function translatedOnTaskTitleAria(translatedTitle: string): string {
    return t`You are working on MAP Accelerator. ${translatedTitle}.`;
}

// Content title ARIA label when On Task and content kind is included in label.
export function translatedOnTaskTitleWithContentKindAria(
    contentKind: LearnableContentKind,
    translatedTitle: string,
): string {
    return t`You are working on MAP Accelerator. ${translatedContentKind(contentKind)}: ${translatedTitle}.`;
}

// Content title ARIA label which includes content kind.
export function translatedTitleWithContentKindAria(
    contentKind: TranslatableContentKind,
    translatedTitle: string,
): string {
    return t`${translatedContentKind(contentKind)}: ${translatedTitle}`;
}

export function errorCode(code?: string | null): React.ReactNode {
    return <Trans> [Error code: {code}]</Trans>;
}

export const courseMasteryPercent = (masteryPercent: number): string => {
    return t`Course mastery: ${masteryPercent}%`;
};

export const unitMasteryPercent = (masteryPercent: number): string => {
    return t`Unit mastery: ${masteryPercent}%`;
};

export const unitMasteryPointsProgress = (
    masteryPointsEarned: number,
    masteryPointsTotal: number,
): string => {
    return t`Unit mastery points: ${numberFormatter.format(masteryPointsEarned)} / ${numberFormatter.format(masteryPointsTotal)}`;
};

export const courseMasteryPointsProgress = (
    masteryPointsEarned: number,
    masteryPointsTotal: number,
): string => {
    return t`Course mastery points: ${numberFormatter.format(masteryPointsEarned)} / ${numberFormatter.format(masteryPointsTotal)}`;
};

export const possibleMasteryPoints = (points: number): string => {
    return t`${numberFormatter.format(points)} possible mastery points`;
};
